// C:\Users\cornn\source\repos\purehealthnexus\src\components\AiDashboard\AiMyNutritionStatus\AiUserFoodFacts.jsx

export const uiConfig = {
  density: 'comfortable',
  size: 'md',
  ctaVariant: 'success',
};

export const sizeToPadding = {
  sm: 'px-3 py-2',
  md: 'px-4 py-2',
  lg: 'px-4 py-3',
};

export const routes = {
  myNutritionStatus: '/my-nutrition-status',
};

export const labels = {
  headerTitle: 'Log Meal',
  heartBtnAriaLabel: 'Open My Nutrition status',
  heartBtnTitle: 'My Nutrition status',

  macrosTitle: 'Macros',
  vitaminsTitle: 'Vitamins & Minerals',
  consumptionTitle: 'Current Consumption',
  suggestedTitle: 'Suggested Consumption',

  aboutToEatTitle: 'About to eat...',
  aboutToEatSubtitle: 'Edit servings, then confirm.',

  toggleSectionAriaLabel: 'Toggle section',
  toggleSectionTitle: 'Toggle section',

  ingredientsBtnAriaLabel: 'Show ingredients',
  ingredientsBtnTitle: 'Show ingredients',
  ingredientsHeaderText: 'INGREDIENTS:',

  decreaseQtyAria: 'Decrease quantity',
  increaseQtyAria: 'Increase quantity',

  confirmBtnText: 'Confirm & Log',
  confirmBtnAriaLabel: 'Confirm and log',
  confirmBtnTitle: 'Confirm and log',

  inputPlaceholder: 'About to eat...',
  inputAriaLabel: 'Log meal input',

  cameraModePlaceholder: 'Click to send image to AI...',

  openCameraAria: 'Open camera',
  openCameraTitle: 'Open camera',
  micAriaLabel: 'Press and hold to speak',
  micTitle: 'Press and hold to speak',
};

// -----------------------------------------------------------------------------
// Daily value reference (config only, can be made region-specific later)
// -----------------------------------------------------------------------------

/**
 * Default fallback daily values (FDA/generic).
 * These are used when user profile targets are not available.
 */
export const FALLBACK_DAILY_VALUE_REFERENCE = {
  energy_kcal: 2000,
  fat_g: 70,
  saturates_g: 20,
  cholesterol_mg: 300,
  carbohydrates_g: 260,
  fibre_g: 30,
  sugars_g: 50,
  protein_g: 50,
  salt_g: 6, // grams of salt (≈ 2.4 g sodium)

  // Vitamins / minerals (approx. EU / NNR-style reference intakes)
  vitaminD_µg: 20,
  calcium_mg: 800,
  iron_mg: 14,
  potassium_mg: 3500,

  vitaminC_mg: 80,
  vitaminA_µg_RE: 800,
  vitaminE_mg_aTE: 12,
  vitaminK_µg: 75,
  magnesium_mg: 375,
  phosphorus_mg: 700,
  zinc_mg: 10,
  selenium_µg: 55,
  folate_µg: 200,
  vitaminB12_µg: 2.5,
};

/**
 * Build daily value reference from user's nutrition targets.
 * Handles both NNR2023 format and comparisonVsTargets format.
 * Falls back to FALLBACK_DAILY_VALUE_REFERENCE if targets are not available.
 *
 * @param {Object} nutritionTargets - User's nutrition targets (from NNR2023 or other source)
 * @returns {Object} Daily value reference object
 */
export function buildDailyValueReference(nutritionTargets = null) {
  const fallback = FALLBACK_DAILY_VALUE_REFERENCE;

  if (!nutritionTargets) {
    return { ...fallback };
  }

  const result = { ...fallback };

  if (nutritionTargets.intakeSummary?.comparisonVsTargets) {
    const comparison = nutritionTargets.intakeSummary.comparisonVsTargets;

    Object.keys(comparison).forEach((key) => {
      const targetValue = comparison[key]?.target;
      if (typeof targetValue === 'number' && targetValue > 0) {
        result[key] = targetValue;
      }
    });
  } else if (nutritionTargets.lifeStage?.nutrients) {
    const nutrients = nutritionTargets.lifeStage.nutrients;

    const mapping = {
      vitaminA: { key: 'vitaminA_µg_RE', field: 'RI' },
      vitaminD: { key: 'vitaminD_µg', field: 'RI' },
      vitaminE: { key: 'vitaminE_mg_aTE', field: 'AI' },
      vitaminK: { key: 'vitaminK_µg', field: 'AI' },
      vitaminC: { key: 'vitaminC_mg', field: 'RI' },
      vitaminB6: { key: 'vitaminB6_mg', field: 'RI' },
      vitaminB12: { key: 'vitaminB12_µg', field: 'AI' },
      folate: { key: 'folate_µg', field: 'RI' },
      thiamin: { key: 'thiamin_mg', field: 'RI_per_MJ' },
      niacin: { key: 'niacin_NE', field: 'RI_per_MJ_NE' },
      pantothenicAcid: { key: 'pantothenicAcid_mg', field: 'AI' },
      biotin: { key: 'biotin_µg', field: 'AI' },
      choline: { key: 'choline_mg', field: 'AI' },
      calcium: { key: 'calcium_mg', field: 'RI' },
      iron: { key: 'iron_mg', field: 'RI' },
      zinc: { key: 'zinc_mg', field: 'RI' },
      copper: { key: 'copper_µg', field: 'RI' },
      magnesium: { key: 'magnesium_mg', field: 'AI' },
      phosphorus: { key: 'phosphorus_mg', field: 'AI' },
      potassium: { key: 'potassium_mg', field: 'AI' },
      iodine: { key: 'iodine_µg', field: 'AI' },
      selenium: { key: 'selenium_µg', field: 'AI' },
      manganese: { key: 'manganese_mg', field: 'AI' },
      molybdenum: { key: 'molybdenum_µg', field: 'AI' },
      fluoride: { key: 'fluoride_mg', field: 'AI' },
    };

    Object.keys(mapping).forEach((nnrKey) => {
      const config = mapping[nnrKey];
      const nutrient = nutrients[nnrKey];

      if (nutrient && typeof nutrient === 'object') {
        const value = nutrient[config.field];
        if (typeof value === 'number' && value > 0) {
          result[config.key] = value;
        }
      }
    });
  }

  return result;
}

/**
 * Default instance using fallback values.
 * This will be replaced with user-specific values when available.
 */
export const DEFAULT_DAILY_VALUE_REFERENCE =
  FALLBACK_DAILY_VALUE_REFERENCE;

// -----------------------------------------------------------------------------
// View-model factory for "About to eat..." cards + Nutrition Facts
// -----------------------------------------------------------------------------

export class AiNutritionViewModelFactory {
  constructor(dailyValueReference = DEFAULT_DAILY_VALUE_REFERENCE) {
    this.dailyValueReference = dailyValueReference;
  }

  /**
   * Update the daily value reference dynamically (per user / profile).
   * Falls back to DEFAULT_DAILY_VALUE_REFERENCE if null or invalid.
   */
  setDailyValueReference(dailyValueReference) {
    if (!dailyValueReference || typeof dailyValueReference !== 'object') {
      this.dailyValueReference = { ...DEFAULT_DAILY_VALUE_REFERENCE };
      return;
    }

    this.dailyValueReference = { ...DEFAULT_DAILY_VALUE_REFERENCE };

    Object.keys(dailyValueReference).forEach((key) => {
      const value = dailyValueReference[key];
      if (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        value > 0
      ) {
        this.dailyValueReference[key] = value;
      }
    });
  }

  createMealCardsFromLastNutrition(lastNutritionJson) {
    const consumedItems = Array.isArray(lastNutritionJson?.consumed)
      ? lastNutritionJson.consumed
      : [];

    return consumedItems.map((consumedItem, index) => {
      const rawLabel =
        typeof consumedItem?.label === 'string' &&
        consumedItem.label.trim()
          ? consumedItem.label.trim()
          : `Item ${index + 1}`;

      const cardTitle = this.toTitleCase(rawLabel);
      const servingText = this.buildServingText(consumedItem?.portion);

      const imageUrl = this.getImageUrlFromConsumedItem(consumedItem);

      const ingredientsList =
        Array.isArray(consumedItem?.items) &&
        consumedItem.items.length > 0
          ? consumedItem.items.map((text) => String(text))
          : [rawLabel];

      const thumbnailText =
        rawLabel.length <= 3
          ? this.toTitleCase(rawLabel)
          : rawLabel.charAt(0).toUpperCase();

      return {
        id:
          typeof consumedItem?.id === 'string' &&
          consumedItem.id.trim()
            ? consumedItem.id
            : `item_${index + 1}`,
        title: cardTitle,
        servingText,
        img: imageUrl,
        ingredients: ingredientsList,
        emoji: imageUrl ? null : thumbnailText,
      };
    });
  }

  createNutritionFactsFromLastNutrition(lastNutritionJson) {
    const consolidatedNutrients =
      lastNutritionJson && lastNutritionJson.consolidated_nutrients
        ? lastNutritionJson.consolidated_nutrients
        : {};

    const consumedItems = Array.isArray(lastNutritionJson?.consumed)
      ? lastNutritionJson.consumed
      : [];

    // Extract product label from consumed items or request text
    let productLabel = '';
    if (Array.isArray(consumedItems) && consumedItems.length === 1) {
      const candidate = consumedItems[0]?.label;
      if (typeof candidate === 'string' && candidate.trim()) {
        productLabel = candidate.trim();
      }
    }

    if (!productLabel && lastNutritionJson?.request?.text) {
      const raw = String(lastNutritionJson.request.text || '');
      const match = raw.match(/LABEL:\s*([^\n;]+)/i);
      if (match && match[1]) {
        productLabel = match[1].trim();
      }
    }

    const totalCalories = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.energy_kcal),
      0
    );

    const totalFatGrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.fat_g),
      2
    );

    const caloriesFromFat = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.calories_from_fat) ||
        this.safeNumber(totalFatGrams) * 9,
      0
    );

    const saturatedFatGrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.saturates_g),
      2
    );
    const transFatGrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.tfa_g),
      2
    );
    const cholesterolMilligrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.cholesterol_mg),
      0
    );
    const totalCarbohydrateGrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.carbohydrates_g),
      2
    );
    const dietaryFibreGrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.fibre_g),
      2
    );
    const totalSugarsGrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.sugars_g),
      2
    );
    const proteinGrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.protein_g),
      2
    );
    const saltGrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.salt_g),
      2
    );

    const vitaminDMicrograms = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.vitaminD_µg),
      2
    );
    const calciumMilligrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.calcium_mg),
      2
    );
    const ironMilligrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.iron_mg),
      2
    );
    const potassiumMilligrams = this.roundToNumber(
      this.safeNumber(consolidatedNutrients.potassium_mg),
      2
    );

    // Derive totalMass, foodMass, and per-100 g food-only values from consumed[]
    const {
      totalMass,
      foodMass,
      per100gFoodEnergy,
      per100gFoodProtein,
    } = this.resolveFoodAndMealMetrics(lastNutritionJson);

    let servingRight = '1 meal';

    if (foodMass > 0 && totalMass > 0 && totalMass > foodMass + 1) {
      servingRight = `1 meal (~${this.roundToNumber(
        foodMass,
        0
      )} g food; ~${this.roundToNumber(
        totalMass,
        0
      )} g incl. water)`;
    } else if (totalMass > 0) {
      servingRight = `1 meal (~${this.roundToNumber(
        totalMass,
        0
      )} g total)`;
    } else if (foodMass > 0) {
      servingRight = `1 meal (~${this.roundToNumber(
        foodMass,
        0
      )} g food)`;
    }

    const per100Energy = this.safeNumber(per100gFoodEnergy);
    const per100Protein = this.safeNumber(per100gFoodProtein);

    const rows = [
      {
        main: 'Total Fat',
        mainBold: true,
        value: `${this.formatNumberAsString(totalFatGrams, 2)}g`,
        pct: this.computePercentDailyValue(totalFatGrams, 'fat_g'),
        indent: 0,
      },
      {
        value: `Saturated Fat ${this.formatNumberAsString(
          saturatedFatGrams,
          2
        )}g`,
        pct: this.computePercentDailyValue(
          saturatedFatGrams,
          'saturates_g'
        ),
        indent: 1,
      },
      {
        value: `Trans Fat ${this.formatNumberAsString(
          transFatGrams,
          2
        )}g`,
        indent: 1,
      },
      { hr: true },
      {
        main: 'Cholesterol',
        mainBold: true,
        value: `${this.formatNumberAsString(
          cholesterolMilligrams,
          0
        )}mg`,
        pct: this.computePercentDailyValue(
          cholesterolMilligrams,
          'cholesterol_mg'
        ),
        indent: 0,
      },
      { hr: true },
      {
        main: 'Salt',
        mainBold: true,
        value: `${this.formatNumberAsString(saltGrams, 2)}g`,
        pct: this.computePercentDailyValue(saltGrams, 'salt_g'),
        indent: 0,
      },
      { hr: true },
      {
        main: 'Total Carbohydrate',
        mainBold: true,
        value: `${this.formatNumberAsString(
          totalCarbohydrateGrams,
          2
        )}g`,
        pct: this.computePercentDailyValue(
          totalCarbohydrateGrams,
          'carbohydrates_g'
        ),
        indent: 0,
      },
      {
        value: `Dietary Fiber ${this.formatNumberAsString(
          dietaryFibreGrams,
          2
        )}g`,
        pct: this.computePercentDailyValue(
          dietaryFibreGrams,
          'fibre_g'
        ),
        indent: 1,
      },
      {
        value: `Total Sugars ${this.formatNumberAsString(
          totalSugarsGrams,
          2
        )}g`,
        pct: this.computePercentDailyValue(
          totalSugarsGrams,
          'sugars_g'
        ),
        indent: 1,
      },
      { hr: true },
      {
        main: 'Protein',
        mainBold: true,
        value: `${this.formatNumberAsString(proteinGrams, 2)}g`,
        pct: this.computePercentDailyValue(proteinGrams, 'protein_g'),
        indent: 0,
      },
    ];

    const baseFacts = [
      {
        keyTxt: 'Vitamin D',
        val: `${this.formatNumberAsString(
          vitaminDMicrograms,
          2
        )}µg`,
        pct: this.computePercentDailyValue(
          vitaminDMicrograms,
          'vitaminD_µg'
        ),
      },
      {
        keyTxt: 'Calcium',
        val: `${this.formatNumberAsString(
          calciumMilligrams,
          2
        )}mg`,
        pct: this.computePercentDailyValue(
          calciumMilligrams,
          'calcium_mg'
        ),
      },
      {
        keyTxt: 'Iron',
        val: `${this.formatNumberAsString(
          ironMilligrams,
          2
        )}mg`,
        pct: this.computePercentDailyValue(ironMilligrams, 'iron_mg'),
      },
      {
        keyTxt: 'Potassium',
        val: `${this.formatNumberAsString(
          potassiumMilligrams,
          2
        )}mg`,
        pct: this.computePercentDailyValue(
          potassiumMilligrams,
          'potassium_mg'
        ),
      },
    ];

    const extraFacts = this.buildExtraMicronutrientFacts(
      consolidatedNutrients,
      ['vitaminD_µg', 'calcium_mg', 'iron_mg', 'potassium_mg']
    );

    const facts = [...baseFacts, ...extraFacts];

    let footnote =
      'The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. A 2,000 calorie reference diet is used for general nutrition advice.';

    if (per100Energy > 0 || per100Protein > 0) {
      const parts = [];
      if (per100Energy > 0) {
        parts.push(
          `${this.formatNumberAsString(
            per100Energy,
            per100Energy >= 10 ? 0 : 1
          )} kcal`
        );
      }
      if (per100Protein > 0) {
        parts.push(
          `${this.formatNumberAsString(
            per100Protein,
            per100Protein >= 10 ? 0 : 1
          )} g protein`
        );
      }
      const refString = parts.join(', ');
      footnote += ` For reference, the food portion (excluding water and drinks) provides about ${refString} per 100 g.`;
    }

    return {
      title: 'Nutrition Facts',
      subTitle: '1 serving per container',
      productLabel,
      servingLeft: 'Serving size',
      servingRight,
      amountPerServing: 'Amount per serving',
      caloriesLabel: 'Calories',
      caloriesValue: String(
        this.roundToNumber(this.safeNumber(totalCalories), 0)
      ),
      caloriesFromFat,
      dailyValueLabel: '% Daily Value*',
      rows,
      facts,
      footnote,
    };
  }

  buildServingText(portion) {
    if (!portion || typeof portion !== 'object') {
      return '1 serving (estimated)';
    }

    const numericQuantity = this.safeNumber(portion.quantity);
    const unitText =
      typeof portion.unit === 'string' && portion.unit.trim()
        ? portion.unit.trim()
        : 'serving';

    const roundedQuantity =
      numericQuantity === 0
        ? 1
        : this.roundToNumber(numericQuantity, 1);

    const formattedQuantity = this.formatNumberAsString(
      roundedQuantity,
      roundedQuantity % 1 === 0 ? 0 : 1
    );

    const isEstimated =
      typeof portion.isEstimated === 'boolean'
        ? portion.isEstimated
        : true;

    const estimationSuffix = isEstimated ? ' (estimated)' : '';

    return `${formattedQuantity} ${unitText}${estimationSuffix}`;
  }

  getImageUrlFromConsumedItem(consumedItem) {
    if (!consumedItem || typeof consumedItem !== 'object') {
      return null;
    }

    if (
      typeof consumedItem.imageUrl === 'string' &&
      consumedItem.imageUrl.trim()
    ) {
      return consumedItem.imageUrl.trim();
    }

    if (
      consumedItem.image &&
      typeof consumedItem.image === 'object' &&
      typeof consumedItem.image.url === 'string' &&
      consumedItem.image.url.trim()
    ) {
      return consumedItem.image.url.trim();
    }

    return null;
  }

  resolvePortionGramEquivalent(labeling, consumedItems) {
    const directValue = this.safeNumber(labeling?.portionGramEquivalent);
    if (directValue > 0) {
      return directValue;
    }
    return this.estimatePortionFromConsumedItems(consumedItems);
  }

  estimatePortionFromConsumedItems(consumedItems) {
    if (!Array.isArray(consumedItems) || consumedItems.length === 0) {
      return 0;
    }

    return consumedItems.reduce((sum, item) => {
      const gramEquivalent = this.safeNumber(
        item?.portion?.gramEquivalent
      );
      if (gramEquivalent > 0) {
        return sum + gramEquivalent;
      }
      return sum;
    }, 0);
  }

  computePercentDailyValueNumber(actualAmount, referenceKey) {
    const numericActualAmount = this.safeNumber(actualAmount);
    const numericReferenceAmount = this.safeNumber(
      this.dailyValueReference?.[referenceKey]
    );

    if (numericActualAmount <= 0 || numericReferenceAmount <= 0) {
      return 0;
    }

    return Math.round(
      (numericActualAmount / numericReferenceAmount) * 100
    );
  }

  computePercentDailyValue(actualAmount, referenceKey) {
    const percent = this.computePercentDailyValueNumber(
      actualAmount,
      referenceKey
    );
    if (!percent) return '';
    return `${percent}%`;
  }

  buildExtraMicronutrientFacts(consolidatedNutrients, excludeKeys = []) {
    const candidates = [
      'vitaminC_mg',
      'vitaminE_mg_aTE',
      'vitaminK_µg',
      'magnesium_mg',
      'phosphorus_mg',
      'zinc_mg',
      'selenium_µg',
      'folate_µg',
      'vitaminB12_µg',
      'vitaminA_µg_RE',
    ].filter((key) => !excludeKeys.includes(key));

    const labelMap = {
      vitaminC_mg: { name: 'Vitamin C', unit: 'mg' },
      vitaminE_mg_aTE: { name: 'Vitamin E', unit: 'mg' },
      vitaminK_µg: { name: 'Vitamin K', unit: 'µg' },
      magnesium_mg: { name: 'Magnesium', unit: 'mg' },
      phosphorus_mg: { name: 'Phosphorus', unit: 'mg' },
      zinc_mg: { name: 'Zinc', unit: 'mg' },
      selenium_µg: { name: 'Selenium', unit: 'µg' },
      folate_µg: { name: 'Folate', unit: 'µg' },
      vitaminB12_µg: { name: 'Vitamin B12', unit: 'µg' },
      vitaminA_µg_RE: { name: 'Vitamin A', unit: 'µg RE' },
    };

    const entries = [];

    for (const key of candidates) {
      const actual = this.safeNumber(consolidatedNutrients?.[key]);
      if (actual <= 0) continue;

      const pctNum = this.computePercentDailyValueNumber(actual, key);
      if (pctNum <= 0) continue;

      const meta = labelMap[key];
      if (!meta) continue;

      entries.push({ key, actual, pctNum, ...meta });
    }

    if (!entries.length) return [];

    entries.sort((a, b) => b.pctNum - a.pctNum);

    const maxExtras = 4;
    return entries.slice(0, maxExtras).map((entry) => {
      const decimals = entry.actual >= 10 ? 0 : 1;
      const formatted = this.formatNumberAsString(
        entry.actual,
        decimals
      );
      return {
        keyTxt: entry.name,
        val: `${formatted}${entry.unit}`,
        pct: `${entry.pctNum}%`,
      };
    });
  }

  toTitleCase(text) {
    if (typeof text !== 'string') {
      return '';
    }

    return text
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => {
        const firstCharacter = word.charAt(0).toUpperCase();
        const restOfWord = word.slice(1);
        return `${firstCharacter}${restOfWord}`;
      })
      .join(' ');
  }

  safeNumber(value) {
    const numericValue =
      typeof value === 'number' ? value : Number(value ?? 0);
    if (!Number.isFinite(numericValue)) {
      return 0;
    }
    return numericValue;
  }

  roundToNumber(value, decimalPlaces) {
    const safeValue = this.safeNumber(value);
    const safeDecimalPlaces =
      typeof decimalPlaces === 'number' && decimalPlaces >= 0
        ? decimalPlaces
        : 0;

    const factor = Math.pow(10, safeDecimalPlaces);
    const rounded = Math.round(safeValue * factor) / factor;
    return rounded;
  }

  formatNumberAsString(value, decimalPlaces) {
    const roundedValue = this.roundToNumber(value, decimalPlaces);
    if (decimalPlaces === 0) {
      return String(Math.round(roundedValue));
    }
    const fixed = roundedValue.toFixed(decimalPlaces);
    return fixed.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }

  /**
   * Classify a consumed item as a likely drink (water, coffee, tea, etc.)
   * based on label text, units, and energy density.
   */
  isLikelyDrinkItem(consumedItem) {
    if (!consumedItem || typeof consumedItem !== 'object') {
      return false;
    }

    if (consumedItem.isDrink === true) {
      return true;
    }

    const labelText = String(consumedItem.label || '').toLowerCase();
    const itemsText = Array.isArray(consumedItem.items)
      ? consumedItem.items.join(' ').toLowerCase()
      : '';
    const combinedText = `${labelText} ${itemsText}`.trim();

    const drinkKeywords = [
      'water',
      'sparkling water',
      'mineral water',
      'coffee',
      'black coffee',
      'tea',
      'green tea',
      'herbal tea',
      'juice',
      'smoothie',
      'soda',
      'cola',
      'lemonade',
      'milk',
      'latte',
      'espresso',
      'cappuccino',
      'americano',
      'broth',
      'stock',
      'wine',
      'beer',
      'cider',
    ];

    if (drinkKeywords.some((keyword) => combinedText.includes(keyword))) {
      return true;
    }

    const portion = consumedItem.portion || {};
    const unitText = String(portion.unit || '').toLowerCase();
    const quantity = this.safeNumber(portion.quantity);
    const grams = this.safeNumber(portion.gramEquivalent);

    const volumeLikeUnits = [
      'cup',
      'cups',
      'glass',
      'glasses',
      'mug',
      'ml',
      'milliliter',
      'milliliters',
      'l',
      'liter',
      'liters',
    ];

    const isVolumeLikeUnit = volumeLikeUnits.includes(unitText);

    const nutrients = consumedItem.nutrients || {};
    const energyKcal = this.safeNumber(nutrients.energy_kcal);

    let energyDensityKcalPer100g = 0;
    if (grams > 0) {
      energyDensityKcalPer100g = (energyKcal / grams) * 100;
    }

    const isVeryLowEnergyDensity =
      grams > 0 && energyDensityKcalPer100g <= 10;

    if (isVolumeLikeUnit && isVeryLowEnergyDensity) {
      return true;
    }

    if (isVeryLowEnergyDensity && quantity >= 1 && grams >= 100) {
      return true;
    }

    return false;
  }

  resolveFoodAndMealMetrics(lastNutritionJson) {
    const labeling = lastNutritionJson?.labeling || {};
    const consumedItems = Array.isArray(lastNutritionJson?.consumed)
      ? lastNutritionJson.consumed
      : [];

    // --- 1) Total mass (meal including drinks) ------------------------------

    let totalMass = this.safeNumber(labeling.portionGramEquivalent);
    if (!totalMass && consumedItems.length > 0) {
      totalMass = this.estimatePortionFromConsumedItems(consumedItems);
    }

    let foodMass = this.safeNumber(labeling.portionGramEquivalentFood);

    if ((!foodMass || (totalMass > 0 && foodMass > totalMass)) && consumedItems.length > 0) {
      const foodItems = [];
      for (const item of consumedItems) {
        if (!item) continue;
        if (this.isLikelyDrinkItem(item)) continue;
        foodItems.push(item);
      }

      let recomputedFoodMass = 0;
      for (const item of foodItems) {
        const grams = this.safeNumber(item?.portion?.gramEquivalent);
        if (grams > 0) {
          recomputedFoodMass += grams;
        }
      }

      if (recomputedFoodMass > 0) {
        foodMass = recomputedFoodMass;
      } else if (totalMass > 0) {
        foodMass = totalMass;
      }
    }

    const per100gFoodLabel =
      (labeling.per100g_food &&
        typeof labeling.per100g_food === 'object' &&
        Object.keys(labeling.per100g_food).length > 0 &&
        labeling.per100g_food) ||
      (labeling.per100g &&
        typeof labeling.per100g === 'object' &&
        Object.keys(labeling.per100g).length > 0 &&
        labeling.per100g) ||
      null;

    let per100gFoodEnergy = 0;
    let per100gFoodProtein = 0;

    if (per100gFoodLabel) {
      per100gFoodEnergy = this.safeNumber(per100gFoodLabel.energy_kcal);
      per100gFoodProtein = this.safeNumber(per100gFoodLabel.protein_g);
    } else if (foodMass > 0 && consumedItems.length > 0) {
      let foodEnergy = 0;
      let foodProtein = 0;

      for (const item of consumedItems) {
        if (!item || this.isLikelyDrinkItem(item)) continue;
        const nutrients = item.nutrients || {};
        foodEnergy += this.safeNumber(nutrients.energy_kcal);
        foodProtein += this.safeNumber(nutrients.protein_g);
      }

      if (foodMass > 0) {
        per100gFoodEnergy = (foodEnergy / foodMass) * 100;
        per100gFoodProtein = (foodProtein / foodMass) * 100;
      }
    } else {
      per100gFoodEnergy = 0;
      per100gFoodProtein = 0;
    }

    return {
      totalMass,
      foodMass,
      per100gFoodEnergy,
      per100gFoodProtein,
    };
  }
}

export const aiNutritionViewModelFactory = new AiNutritionViewModelFactory(
  DEFAULT_DAILY_VALUE_REFERENCE
);

export function createMealCardsFromLastNutrition(lastNutritionJson) {
  return aiNutritionViewModelFactory.createMealCardsFromLastNutrition(
    lastNutritionJson
  );
}

export function createNutritionFactsFromLastNutrition(lastNutritionJson) {
  return aiNutritionViewModelFactory.createNutritionFactsFromLastNutrition(
    lastNutritionJson
  );
}
